/**
 * FeaturedBannerCarousel — premium home-page hero with depth + motion.
 *
 * Visual upgrades over the previous flat coloured strip:
 *  • Full-bleed cinematic image with a slow Ken-Burns zoom.
 *  • Soft animated gradient orbs floating behind the image (parallax feel).
 *  • Diagonal shimmer sweep across the panel every 7s.
 *  • Glass-morphism content card with glowing border using the banner accent.
 *  • Animated H1 with "apt-title" gradient sweep + entrance lift.
 *  • Pill CTA with hover gradient slide + arrow micro-animation.
 *  • Pagination as elegant counters (01 / 03) instead of dots only.
 *  • Auto-rotates every 6s, pauses on hover/focus, respects reduced-motion.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import { AnimatedPageTitle } from './ui/AnimatedPageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROTATE_MS = 6000;

export default function FeaturedBannerCarousel() {
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/banners/active`)
      .then((res) => setBanners(res.data?.banners || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length > 1 && !isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrent((p) => (p + 1) % banners.length);
      }, ROTATE_MS);
    }
    return () => clearInterval(intervalRef.current);
  }, [banners.length, isPaused]);

  if (banners.length === 0) return null;

  const b = banners[current];
  const accent = b.accent_color || '#FFDA33';
  const secondary = b.secondary_color || '#00CCFF';

  const handleCTA = () => {
    if (b.cta_link?.startsWith('http')) {
      window.open(b.cta_link, '_blank', 'noopener');
    } else {
      navigate(b.cta_link || '/browse');
    }
  };

  return (
    <section
      className="fb-carousel dark-surface relative overflow-hidden border-b border-[#1a1a1a] bg-[#050505]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
        touchDeltaX.current = 0;
        setIsPaused(true);
      }}
      onTouchMove={(e) => {
        touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
      }}
      onTouchEnd={() => {
        const dx = touchDeltaX.current;
        if (Math.abs(dx) > 40 && banners.length > 1) {
          if (dx < 0) setCurrent((p) => (p + 1) % banners.length);
          else setCurrent((p) => (p - 1 + banners.length) % banners.length);
        }
        setIsPaused(false);
      }}
      data-testid="featured-banner-carousel"
      style={{ '--fb-accent': accent, '--fb-secondary': secondary }}
    >
      {/* Sliding stage — each banner is absolutely positioned, fades in */}
      <div className="relative h-[260px] sm:h-[320px] md:h-[380px]">
        {banners.map((bn, i) => (
          <div
            key={bn.id || i}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              i === current ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
            aria-hidden={i !== current}
          >
            {/* Solid base colour fallback */}
            <div
              className="absolute inset-0"
              style={{ background: bn.bg_color || '#0a0a0a' }}
            />
            {/* Cinematic image with slow Ken-Burns zoom */}
            {bn.image_url && (
              <img
                src={bn.image_url}
                alt=""
                className="fb-kenburns absolute inset-0 w-full h-full object-cover"
                style={{ opacity: 0.55 }}
              />
            )}
            {/* Animated gradient orbs (parallax depth) */}
            <div
              className="fb-orb fb-orb--a absolute pointer-events-none"
              style={{ background: `radial-gradient(circle, ${accent}55, transparent 60%)` }}
            />
            <div
              className="fb-orb fb-orb--b absolute pointer-events-none"
              style={{ background: `radial-gradient(circle, ${secondary}55, transparent 60%)` }}
            />
            {/* Bottom-right diagonal shimmer */}
            <div className="fb-shimmer absolute inset-0 pointer-events-none" />
            {/* Bottom gradient for content readability */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(120deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.15) 100%)',
              }}
            />
          </div>
        ))}

        {/* Foreground content (re-renders on slide change → entrance animations re-fire) */}
        <div
          key={`fb-content-${current}`}
          className="relative z-20 h-full max-w-[1600px] mx-auto px-6 md:px-10 flex items-center"
        >
          <div className="fb-card max-w-2xl">
            {/* "Featured" pill */}
            <div className="fb-pill inline-flex items-center gap-1.5 mb-3">
              <Sparkles size={12} style={{ color: accent }} />
              <span
                className="font-mono text-[10px] tracking-[0.3em] uppercase"
                style={{ color: accent }}
              >
                Featured
              </span>
            </div>
            <AnimatedPageTitle
              accent={accent}
              secondary={secondary}
              testId="banner-title"
              className="!text-2xl sm:!text-3xl md:!text-5xl !leading-tight"
            >
              {b.title}
            </AnimatedPageTitle>
            {b.subtitle && (
              <p
                className="fb-sub mt-3 text-sm sm:text-base md:text-lg font-sans text-white/80 max-w-xl leading-relaxed"
                data-testid="banner-subtitle"
              >
                {b.subtitle}
              </p>
            )}
            <button
              onClick={handleCTA}
              className="fb-cta group mt-5 inline-flex items-center gap-2 px-6 py-2.5 font-['Chakra_Petch'] uppercase text-sm tracking-[0.18em] rounded-full overflow-hidden relative"
              style={{
                color: '#fff',
                background: `linear-gradient(95deg, ${accent} 0%, ${secondary} 100%)`,
                boxShadow: `0 6px 20px ${accent}55, 0 0 0 1px ${accent}66 inset`,
              }}
              data-testid="banner-cta"
            >
              <span className="relative z-10">{b.cta_text || 'Explore'}</span>
              <ArrowRight
                size={16}
                className="relative z-10 transition-transform duration-300 group-hover:translate-x-1.5"
              />
              <span className="fb-cta-sheen absolute inset-0" />
            </button>
          </div>
        </div>

        {/* Navigation arrows — hidden on mobile (would overlap title text);
            on mobile, slide indicators + auto-rotate handle navigation. */}
        {banners.length > 1 && (
          <>
            <button
              onClick={() => setCurrent((p) => (p - 1 + banners.length) % banners.length)}
              className="fb-nav hidden md:flex absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-30"
              aria-label="Previous banner"
              data-testid="banner-prev"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => setCurrent((p) => (p + 1) % banners.length)}
              className="fb-nav hidden md:flex absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-30"
              aria-label="Next banner"
              data-testid="banner-next"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Counter + bars */}
        {banners.length > 1 && (
          <div className="absolute bottom-4 right-5 z-30 flex items-center gap-3 font-mono text-[11px] tracking-[0.2em] uppercase text-white/85">
            <span className="text-white" data-testid="banner-counter">
              {String(current + 1).padStart(2, '0')}
            </span>
            <span className="text-white/30">/</span>
            <span className="text-white/60">{String(banners.length).padStart(2, '0')}</span>
            <div className="ml-2 flex gap-1">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-[3px] rounded-sm transition-all duration-500 ${
                    i === current ? 'w-8 bg-white' : 'w-3 bg-white/35 hover:bg-white/60'
                  }`}
                  data-testid={`banner-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
